import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, OpenInNew, Refresh } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  attendFulfillmentPickupCode,
  getFulfillmentPickupCodes,
} from '../../services/fulfillment';
import CustomPagination from '../../components/common/CustomPagination';

const PickupCodes = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [attending, setAttending] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFulfillmentPickupCodes({
        status: statusFilter,
        search: search.trim() || undefined,
        page: page + 1,
        page_size: rowsPerPage,
      });
      const data = response.results || response || [];
      setRows(data);
      setTotal(response.count ?? data.length);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load pickup codes',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter]);

  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(String(rowsPerPage));
      return;
    }
    setRowsPerPage(Number(value));
    setPage(0);
    setCustomRowsActive(false);
  };

  const handleCustomRowsInputChange = (event) => {
    setCustomRowsInput(event.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const markAttended = async (row) => {
    setAttending(row.sid);
    try {
      await attendFulfillmentPickupCode(row.fulfillment_request.sid, row.sid);
      setSnackbar({ open: true, message: 'Pickup code marked as attended', severity: 'success' });
      await loadCodes();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Unable to mark pickup code as attended',
        severity: 'error',
      });
    } finally {
      setAttending(null);
    }
  };

  const columns = [
    {
      field: 'pickup_code',
      headerName: 'Pickup Number',
      minWidth: 155,
      flex: 1,
      renderCell: ({ value }) => <Typography fontFamily="monospace" fontWeight={700}>{value}</Typography>,
    },
    {
      field: 'request_number',
      headerName: 'Request',
      minWidth: 165,
      flex: 1,
      valueGetter: (_value, row) => row.fulfillment_request?.request_number || '',
    },
    { field: 'tracking_number', headerName: 'Tracking Number', minWidth: 190, flex: 1 },
    {
      field: 'customer',
      headerName: 'Customer',
      minWidth: 220,
      flex: 1.2,
      valueGetter: (_value, row) => row.user?.display_name || row.user?.email || '',
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{row.user?.display_name || 'N/A'}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {row.user?.bin_code ? `BIN: ${row.user.bin_code}` : row.user?.email || ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'pickup_code_submitted_at',
      headerName: 'Submitted',
      minWidth: 175,
      flex: 1,
      renderCell: ({ value }) => value ? new Date(value).toLocaleString() : 'N/A',
    },
    {
      field: 'pickup_code_status',
      headerName: 'Status',
      width: 125,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value === 'attended' ? 'Attended' : 'Pending'}
          color={value === 'attended' ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 245,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<OpenInNew />}
            onClick={() => navigate(`/admin/fulfillment/requests?request=${row.fulfillment_request.sid}`)}
          >
            Open Request
          </Button>
          {row.pickup_code_status !== 'attended' && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={attending === row.sid ? <CircularProgress size={15} color="inherit" /> : <CheckCircle />}
              disabled={attending === row.sid}
              onClick={() => markAttended(row)}
            >
              Attended
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden',
    }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 8 }}>
          <Typography variant="h4" fontWeight="bold" color="primary.main">Pickup Codes</Typography>
          <Typography color="text.secondary">Every pending row needs warehouse attention.</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadCodes} disabled={loading}>Refresh</Button>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 5, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(event) => { setStatusFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="attended">Attended</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 7, md: 9 }}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Pickup, tracking, request or customer"
                value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(0); }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {statusFilter === 'pending' && rows.length === 0 && !loading && (
        <Alert severity="success" sx={{ mb: 2 }}>All submitted pickup codes have been attended to.</Alert>
      )}

      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ width: '100%', overflowX: 'auto', minHeight: 420 }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              getRowId={(row) => row.sid}
              loading={loading}
              hideFooter
              disableRowSelectionOnClick
              sx={{
                border: 0,
                minWidth: 1100,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: 'primary.main',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'primary.contrastText',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'primary.contrastText',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <CustomPagination
        page={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        onCustomRowsInputChange={handleCustomRowsInputChange}
        onCustomRowsInputBlur={handleCustomRowsInputBlur}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((current) => ({ ...current, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PickupCodes;
